import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useAsyncStateSupabaseCallback } from "../../utils/hooks/use-async-state-callback";
import yup from "../../utils/yup";
import { CeTextInput } from "../generic/components/form-fields/ce-text-field";
import { SupabaseFolderService } from "./server/folder-service";

import type { UUID } from "../../utils/uuid";
import type { FC } from "react";

export interface CreateFolderData {
  parentFolderId: UUID | null;
}

export const CreateFolderModal: FC<{
  open: boolean;
  onClose: () => void;
  reloadFolders: () => void;
  parentFolderId: UUID | null;
}> = ({ open, onClose, reloadFolders, parentFolderId }) => {
  const methods = useForm({
    defaultValues: { name: "" },
    resolver: yupResolver(
      yup.object({
        name: yup.string().required("Enter the folder name").label("Name"),
      }),
    ),
  });

  const {
    handleSubmit,
    reset,
    formState: { errors },
  } = methods;

  const { loading: saving, load: submit } = useAsyncStateSupabaseCallback(
    async ({ supabase }, { name }: { name: string }) => {
      const service = new SupabaseFolderService(supabase);

      const { error } = await service.insert({
        parent_folder_id: parentFolderId,
        name,
      });

      if (error) {
        console.error(error);

        if (error.message.includes("folder_unique_name")) {
          toast.error("Folder name must be unique");
        } else {
          toast.error("Error when creating folder");
        }

        return false;
      }

      toast.success(`Folder '${name}' created`);
      reloadFolders();
      onClose();
      reset();
      return true;
    },
    [onClose, reset, parentFolderId],
  );

  return (
    <Dialog
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <FormProvider {...methods}>
        <form id="custom-script-form" onSubmit={handleSubmit(submit)}>
          <DialogTitle>Create new folder</DialogTitle>
          <DialogContent>
            <CeTextInput
              name="name"
              analyticsCategory="content_library"
              margin="dense"
              label="Name"
              fullWidth
              {...(errors.name && { helperText: errors.name.message })}
            />
          </DialogContent>
          <DialogActions>
            <Stack
              direction="row"
              spacing={2}
              sx={{ width: "100%", p: 1, justifyContent: "flex-end" }}
            >
              <Button
                data-analytics-id="create-folder-cancel"
                variant="text"
                color="error"
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                data-analytics-id="create-folder-confirm"
                variant="text"
                type="submit"
                loading={saving}
              >
                Create folder
              </Button>
            </Stack>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};
